import { Button } from '@pella/sash-framework';
import { useTranslation } from 'react-i18next';
import styles from './EquipActionForm.module.css';

/**
 * Form component for equipment actions
 * @param {object} root0 - Component props
 * @param {string} root0.comment - Comment text
 * @param {(e: import('react').ChangeEvent<HTMLTextAreaElement>) => void} root0.onCommentChange - Callback for comment changes
 * @param {string} root0.empNumber - Employee number
 * @param {(e: import('react').ChangeEvent<HTMLInputElement>) => void} root0.onEmpNumberChange - Callback for employee number changes
 * @param {object} root0.serviceAreaSelected - Selected service area
 * @param {object} root0.serviceActionSelected - Selected service action
 * @param {Array<object>} root0.filteredServiceAreas - Filtered service areas list
 * @param {Array<object>} root0.filteredServiceActions - Filtered service actions list
 * @param {(area: object) => void} root0.onServiceAreaChange - Callback for service area changes
 * @param {(action: object) => void} root0.onServiceActionChange - Callback for service action changes
 * @param {object} root0.selectedEquipment - Selected equipment object
 * @param {boolean} root0.isSubmitting - Whether form is currently submitting
 * @param {boolean} root0.loadingCreateConversation - Whether conversation creation is loading
 * @param {boolean} root0.isMaintenanceCopilotFlagEnabled - Whether maintenance copilot is enabled
 * @param {(event: import('react').FormEvent) => void} root0.onSubmit - Form submission handler
 * @param {() => void} root0.onCancel - Cancel handler
 * @param {() => void} root0.onCreateConversation - Create conversation handler
 * @returns {import('react').ReactElement} The EquipActionForm component
 */
const EquipActionForm = ({
  comment,
  onCommentChange,
  empNumber,
  onEmpNumberChange,
  serviceAreaSelected,
  serviceActionSelected,
  filteredServiceAreas,
  filteredServiceActions,
  onServiceAreaChange,
  onServiceActionChange,
  selectedEquipment,
  isSubmitting,
  loadingCreateConversation,
  isMaintenanceCopilotFlagEnabled,
  onSubmit,
  onCancel,
  onCreateConversation,
}) => {
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit}>
      <div className={styles.formContainer}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>{t('employeeNumber')}*</label>
          <input
            id='emp-num'
            name='emp-num'
            className={`${styles.formInput} ${styles.narrowField}`}
            type='text'
            value={empNumber}
            onChange={onEmpNumberChange}
            required
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('selectServiceArea')}*</label>
            <select
              id='combo-box-service-area'
              name='serviceArea'
              className={styles.selectInput}
              value={serviceAreaSelected?.serviceArea || ''}
              onChange={(e) => onServiceAreaChange({ ...serviceAreaSelected, serviceArea: e.target.value })}
              required
            >
              <option value=''>{t('notSelected')}</option>
              {filteredServiceAreas.map((area) => (
                <option key={area.serviceArea} value={area.serviceArea}>
                  {t(area.i18nTranslationKey)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('selectServiceAction')}*</label>
            <select
              id='combo-box-service-action'
              name='serviceAction'
              className={styles.selectInput}
              value={serviceActionSelected?.itemValue || ''}
              onChange={(e) => {
                const selected = filteredServiceActions.find((a) => a.itemValue === e.target.value);
                onServiceActionChange(selected || {});
              }}
              required
            >
              <option value=''>{t('notSelected')}</option>
              {filteredServiceActions.map((action) => (
                <option key={action.itemValue} value={action.itemValue}>
                  {t(action.i18nTranslationKey)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>{t('comment')}*</label>
          <textarea
            id='comment'
            name='comment'
            className={styles.formInput}
            required
            rows={3}
            value={comment}
            onChange={onCommentChange}
          />
        </div>

        <div className={styles.buttonContainer}>
          <Button color='secondary' size='sm' onClick={onCancel}>
            {t('cancel')}
          </Button>
          {isMaintenanceCopilotFlagEnabled ? (
            <Button
              type='button'
              color='primary'
              size='sm'
              disabled={
                !selectedEquipment ||
                !empNumber?.trim() ||
                !serviceAreaSelected?.serviceArea ||
                !serviceActionSelected?.itemValue ||
                !comment?.trim() ||
                loadingCreateConversation ||
                isSubmitting
              }
              onClick={onCreateConversation}
            >
              {loadingCreateConversation || isSubmitting ? `${t('loading')}...` : t('continue')}
            </Button>
          ) : (
            <Button
              color='primary'
              size='sm'
              type='submit'
              disabled={
                !selectedEquipment ||
                !empNumber?.trim() ||
                !serviceAreaSelected?.serviceArea ||
                !serviceActionSelected?.itemValue ||
                !comment?.trim() ||
                isSubmitting
              }
            >
              {isSubmitting ? `${t('saving')}...` : t('submit')}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};

export default EquipActionForm;
